import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DxRadioGroupModule, DxValidatorModule} from "devextreme-angular";
import {
    ChecklistClient,
    ChecklistQuestionAnswerDto,
    ChecklistQuestionAnswerType,
    ChecklistQuestionGroupDto
} from "@services";
import {firstValueFrom} from "rxjs";
import notify from "devextreme/ui/notify";
import {ChecklistQuestionGroupTypePipe} from "../../../../../../pipes/checklist-question-group-type.pipe";

@Component({
    selector: 'app-checklist-questions',
    standalone: true,
    imports: [
        CommonModule,
        DxRadioGroupModule,
        DxValidatorModule,
        ChecklistQuestionGroupTypePipe
    ],
    templateUrl: './checklist-questions.component.html',
    styles: [`
        .question-row {
            min-height: 40px;
        }
    `]
})
export class ChecklistQuestionsComponent implements OnChanges {
    private checklistsClient = inject(ChecklistClient);

    @Input() assetId: string | null | undefined;
    @Input() initialAnswers: ChecklistQuestionAnswerDto[] = [];
    @Input() readOnly = false;
    @Input() includeRadioShuttleQuestions = false;
    @Input() includeTwoWayRadioQuestions = false;

    protected questionGroups: ChecklistQuestionGroupDto[] = [];
    protected answers: Record<string, ChecklistQuestionAnswerDto> = {};
    protected isLoading = false;

    protected answerTypeOptions = [
        {value: ChecklistQuestionAnswerType.Ok, text: 'OK'},
        {value: ChecklistQuestionAnswerType.NeedsService, text: 'Needs Service'}
    ];

    async ngOnChanges(changes: SimpleChanges) {
        if (changes['assetId'] || changes['includeRadioShuttleQuestions'] || changes['includeTwoWayRadioQuestions']) {
            if (this.assetId) {
                await this.loadQuestions(this.assetId, this.includeRadioShuttleQuestions, this.includeTwoWayRadioQuestions);
            } else {
                this.questionGroups = [];
                this.answers = {};
            }
        }

        if (changes['initialAnswers'] && this.initialAnswers?.length) {
            this.mapAnswersToDictionary(this.initialAnswers);
        }
    }

    private async loadQuestions(assetId: string, includeRadioShuttleQuestions: boolean, includeTwoWayRadioQuestions: boolean) {
        try {
            this.isLoading = true;
            this.questionGroups = await firstValueFrom(this.checklistsClient.getChecklistQuestions(assetId, includeRadioShuttleQuestions, includeTwoWayRadioQuestions));
            this.initializeDefaultAnswers();
        } catch (e) {
            notify('Failed to load questions', 'error', 3000);
        } finally {
            this.isLoading = false;
        }
    }

    private initializeDefaultAnswers() {
        this.answers = {};
        this.questionGroups.forEach(group => {
            group.questions?.forEach(q => {
                if (q.questionId) {
                    this.answers[q.questionId] = new ChecklistQuestionAnswerDto({
                        questionId: q.questionId,
                        answer: undefined
                    });
                }
            });
        });

        if (this.initialAnswers && this.initialAnswers.length > 0) {
            this.mapAnswersToDictionary(this.initialAnswers);
        }
    }

    private mapAnswersToDictionary(answersList: ChecklistQuestionAnswerDto[]) {
        if (!answersList) return;
        answersList.forEach(a => {
            if (a.questionId) {
                this.answers[a.questionId] = new ChecklistQuestionAnswerDto({...a});
            }
        });
    }

    public getAnswers(): ChecklistQuestionAnswerDto[] {
        return Object.values(this.answers).filter(a => a.answer !== undefined);
    }

    public get hasDefects(): boolean {
        return Object.values(this.answers).some(a => a.answer === ChecklistQuestionAnswerType.NeedsService);
    }

    public clear() {
        this.questionGroups = [];
        this.answers = {};
    }
}
